import React, { useEffect, useMemo, useRef, useState } from 'react'
import { TSchedule, scheduleType, schedules } from '../../shared/types/schedule.type'
import {
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography
} from '@mui/material'
import {
    Email,
    EventRepeat,
    MoreHoriz,
    Send,
    Visibility,
    VisibilityOff,
    WhatsApp
} from '@mui/icons-material'
import { t } from 'i18next'
import { weekDays } from '../../enums/days.enums'
import { TChannel } from '../../shared/types/Channel.type'
import { flatten, get, groupBy, isArray, isEmpty, isNumber, last, sortBy, upperCase } from 'lodash'
import ShowMore from './ShowMore'
import { Icon } from '@iconify/react'
import gsap from 'gsap'
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { LoadingButton } from '@mui/lab'
import { useAuth } from '../../hooks/useAuth'

const { RangePicker } = DatePicker;
function ConfigItem({ config, toggleEnabled, onDelete, onEdit, isDeleting, onDuplicate, triggerSend, isTriggering }: {
    config: TSchedule,
    isTriggering: boolean,
    children?: React.ReactNode,
    isDeleting?: boolean,
    onDelete?: (id: number, type?: string) => void,
    onEdit?: (item: TSchedule) => void,
    onDuplicate?: (item: TSchedule) => void,
    toggleEnabled?: (id: number, value: boolean) => void,
    triggerSend: (id: number, value: string[]) => void
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [openConfig, setOpenConfig] = useState(false)
    const [openRecipient, setOpenRecipient] = useState(false)

    const channels = get(config, 'communication_channels', []) as TChannel[]
    const today = dayjs();
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef<HTMLDivElement | null>(null);
    const [value, setValue] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([today, today]);
    const { auth } = useAuth()
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (showPicker && pickerRef.current) {
            requestAnimationFrame(() => {
                gsap.fromTo(
                    pickerRef.current,
                    { x: 150, opacity: 0 },
                    {
                        x: 0, opacity: 1, duration: 0.4,
                        ease: "back.out(1.2)", // subtle spring
                    }
                );
            })

        }
    }, [showPicker]);



    const horseNames = useMemo(() => {
        if (!config?.horses) return []

        return config.horses.map(e => {

            let horseName = get(e, 'name', '')

            const trainer_mail = get(e, 'trainer_email')

            if (trainer_mail && trainer_mail === auth?.email) {
                horseName.concat(` (${trainer_mail})`)
            }

            return horseName

        })
    }, [config.horses, auth?.email])

    const tracks = useMemo(() => {
        if (!config?.track_names) return []

        return config.track_names.map(e => {
            let displayName = get(e, 'display_name', '')

            const trainer_mail = get(e, 'trainer_email')

            if (trainer_mail && trainer_mail === auth?.email) {
                displayName.concat(` (${trainer_mail})`)
            }

            return displayName

        })
    }, [config.track_names, auth?.email])


    const trainings = useMemo(() => {
        if (!config?.training_types) return []

        return config.training_types.map(e => {
            let displayName = get(e, 'display_name', '')

            const trainer_mail = get(e, 'trainer_email')

            if (trainer_mail && trainer_mail === auth?.email) {
                displayName.concat(` (${trainer_mail})`)
            }

            return displayName

        })
    }, [config.training_types, auth?.email])

    const getResume = () => {
        let schedules = config.schedules

        if (isEmpty(schedules)) return ''

        let schedule_type = schedules[0].type as scheduleType

        if (schedule_type === 'SYNCHRONISED_LAST_HOUR') {
            if (schedules.length === weekDays.length) {
                return t('pdfReports:emails:scheduleDescriptionSynchronisedLastHour')
            } else {

                let days: number[] = sortBy(schedules.map(e => e.day_of_week)) as number[]

                return t('pdfReports:emails:scheduleDescriptionSynchronisedLastHourEveryday', {
                    0: getDaysStr(days)
                })

            }
        }
        if (schedule_type === 'FIXED_TIME') {


            let groupedBytime = groupBy(schedules, i => String(i.hour) + String(i.minute))


            let values = Object.values(groupedBytime)

            if (isEmpty(values)) return ''

            //Get all with 7 days

            let WithAllWeek = values.filter(e => e.length === 7)
            let notAllWeek = values.filter(e => e.length !== 7)



            if (!isEmpty(WithAllWeek)) {

                return t('pdfReports:emails:scheduleDescriptionFixedTimeEveryday', {
                    1: getTime(WithAllWeek).concat(!isEmpty(notAllWeek) ? ' , ' : '').concat(getOtherTime(notAllWeek))
                })

            } else {
                if (isEmpty(notAllWeek)) return ""

                let firstEl = notAllWeek[0]
                let days: number[] = sortBy(firstEl.map(e => e.day_of_week)) as number[]

                let slicedNotAllWeek = notAllWeek.slice(1, notAllWeek.length)

                return t('pdfReports:emails:scheduleDescriptionFixedTime', {
                    0: getDaysStr(days),
                    1: getTime([firstEl]).concat(!isEmpty(slicedNotAllWeek) ? ' , ' : '').concat(getOtherTime(slicedNotAllWeek))
                })
            }
        }
        if (schedule_type === 'LAST_WEEK') {

            return t('pdfReports:emails:scheduleDescriptionLastWeek', {
                0: getDaysStr(schedules.map(e => e.day_of_week) as number[]),
                1: getTime([schedules])
            })
        }

        if (schedule_type === 'LAST_X_HOURS') {

            let schedule_hours = schedules[0]?.count_hours_before

            let groupedBytime = groupBy(schedules, i => String(i.hour) + String(i.minute))


            let values = Object.values(groupedBytime)

            if (isEmpty(values)) return ''

            //Get all with 7 days

            let WithAllWeek = values.filter(e => e.length === 7)
            let notAllWeek = values.filter(e => e.length !== 7)



            if (!isEmpty(WithAllWeek)) {

                return t('pdfReports:emails:scheduleDescriptionxHoursEveryday', {
                    0: schedule_hours,
                    1: getTime(WithAllWeek).concat(!isEmpty(notAllWeek) ? ' , ' : '').concat(getOtherTime(notAllWeek))
                })

            } else {
                if (isEmpty(notAllWeek)) return ""

                let firstEl = notAllWeek[0]
                let days: number[] = sortBy(firstEl.map(e => e.day_of_week)) as number[]

                let slicedNotAllWeek = notAllWeek.slice(1, notAllWeek.length)

                return t('pdfReports:emails:scheduleDescriptionxHours', {
                    0: schedule_hours,
                    1: getDaysStr(days),
                    2: getTime([firstEl]).concat(!isEmpty(slicedNotAllWeek) ? ' , ' : '').concat(getOtherTime(slicedNotAllWeek))
                })
            }



        }

        if (schedule_type === 'MONTHLY') {
            let firstSchedule = get(schedules, '[0]', null)

            if (!firstSchedule) return ""
            return t('pdfReports:emails:monthly', {
                day: firstSchedule.day_of_month,
                time: dayjs().set('hour', firstSchedule?.hour ?? 0).set('minute', firstSchedule?.minute ?? 0).format('HH:mm')
            })

        }
        if (schedule_type === 'BI_MONTHLY') {
            let firstSchedule = get(schedules, '[0]', null)

            return t('pdfReports:emails:bimonthly', {
                time: dayjs().set('hour', firstSchedule?.hour ?? 0).set('minute', firstSchedule?.minute ?? 0).format('HH:mm')
            })
        }

    }

    const getOtherTime = (notAllWeek: Partial<schedules>[][]) => {
        if (isEmpty(notAllWeek)) return ""

        let str: string[] = []

        notAllWeek.forEach(i => {
            let days: number[] = sortBy(i.map(e => e.day_of_week)) as number[]

            const daysStr = getDaysStr(days)

            const timeStr = getTime([i])

            let concated = daysStr.concat(' ').concat(t('at')).concat(' ').concat(timeStr)

            str.push(concated)


        })

        return str.join(' , ')
    }

    const getTime = (dates: Partial<schedules>[][]) => {
        let time: string[] = []

        dates.forEach(date => {

            let minutes = date[0].minute == 0 ? '00' : String(date[0].minute)

            time.push(String(date[0].hour).concat(':').concat(minutes))
        })

        return time.join(' , ')
    }

    const getDaysStr = (days: number[]) => {

        let str = ''
        if (days.length === 1) {
            str = t(weekDays.find(e => e.value === days[0])?.key ?? "")
        } else {
            let useWithComma = [...days].slice(0, days.length - 1).map((i) => {
                let day = weekDays.find(e => e.value === i)
                return day ? t(day.key) : ''
            })

            str = useWithComma.join(' , ')

            str += ' ' + t('and') + ' ' + t(weekDays.find(e => e.value === days[days.length - 1])?.key ?? "")
        }

        return str

    }

    const getConfigRecap = () => {
        let str: string[] = []

        str.push(config.horses.length === 0 ? t('configuration:allHorses') : t('configuration:someHorses'))
        str.push(config.track_names.length === 0 ? t('configuration:allTracks') : t('configuration:someTracks'))
        str.push(config.training_types.length === 0 ? t('configuration:allTypes') : t('configuration:someTypes'))

        if ("horse_ages" in config && isArray(config?.horse_ages)) {


            str.push(isEmpty(config.horse_ages) ? t('configuration:allAges') : t('configuration:someAges', {
                ages: formatHorsesAgesStr(config.horse_ages)
            }))
        }

        return ` (${str.join(' - ')})`

    }

    const formatHorsesAgesStr = (ages: number[] = []) => {
        let formattedAges = ""

        if (ages.length === 1) {
            formattedAges = ages[0].toString()
        }

        if (ages.length > 1) {

            const sortedAges = ages.sort((a, b) => a - b);

            const rest = sortedAges.slice(0, -1);
            const lastElement = sortedAges.at(-1);

            formattedAges += rest.join(', ').concat(` ${t('and')} `).concat((lastElement ?? "").toString())
        }
        return formattedAges
    }

    const getTruncateStr = (str: string) => {
        try {

            let firstChar = str.charAt(0);
            let lastChar = str.charAt(str.length - 1);

            return upperCase(firstChar).concat(upperCase(lastChar))
        } catch (error) {
            return str
        }

    }

    const getChannelsRecap = () => {
        if (isEmpty(channels)) return <></>

        return (
            <Box ml={1} display='flex' gap={1} flexWrap={'wrap'} alignItems={'center'}>
                <span>(</span>
                {channels.map(c => (
                    c.targets.map(target => (
                        <Chip size='small'
                            key={target}
                            avatar={<Tooltip title={c.name} arrow><Avatar
                                sx={{
                                    backgroundColor: c['@type'] === "EmailCommunicationChannel" ? '#0072C6' : '#25D366',
                                    color: 'white !important', // ✅ force text color
                                }}>{getTruncateStr(c.name)}</Avatar></Tooltip>}
                            label={target}></Chip>
                    ))
                ))}
                <span>)</span>

            </Box>
        )


    }

    const getConfigId = () => {

        let id = null

        if ('id' in config) {
            id = config.id
        } else {
            let strId = config['@id'] as string
            id = Number(last(strId.split('/')))
        }
        return id

    }

    const getFormattedDates = () => {
        return value.map(date => date.utc(true).format('YYYY-MM-DD'))
    }

    return (
        <Box sx={{ border: "1px solid var(--active)", borderRadius: 2, overflow: 'hidden' }}>
            <Box display={'flex'} alignItems={'center'} px={3} py={2} gap={1} justifyContent={'space-between'}
                flexWrap={'wrap'}>

                <Typography variant='subtitle1' display={'flex'} alignItems={'center'}
                    fontWeight={500}>{config.name}</Typography>


                <Box display={'flex'} gap={1} flexWrap={'wrap'}>

                    <Box display={'flex'} gap={1} flexWrap={'wrap'}>

                        {showPicker && (
                            <Box ref={pickerRef}>
                                <RangePicker
                                    value={value}
                                    allowClear={false}
                                    onChange={(dates) => {
                                        if (dates) setValue(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                                    }}
                                    style={{ height: 36 }}
                                />
                            </Box>
                        )}
                        {!showPicker ? (
                            <LoadingButton
                                loading={isTriggering}
                                variant="outlined"
                                size="small"
                                onClick={() => setShowPicker(true)}
                                startIcon={<Icon icon="bi:send-plus" />}
                            >
                                {t('button:triggerNow')}
                            </LoadingButton>
                        ) : (
                            <Box display={'flex'} gap={1} flex={{ xs: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    startIcon={<Icon icon="bi:send" />}
                                    onClick={() => {
                                        triggerSend(getConfigId(), getFormattedDates())
                                        setShowPicker(false)
                                    }}

                                >
                                    {t('button:send')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        setShowPicker(false)
                                        setValue([today, today])
                                    }}
                                >
                                    {t('cancel')}
                                </Button>
                                <Divider
                                    sx={{ display: { xs: "none", md: "block" } }}
                                    orientation='vertical' />
                            </Box>
                        )}
                    </Box>

                    <Button
                        size='small'
                        variant='outlined'
                        sx={{ justifySelf: 'flex-end' }}
                        id="basic-button"
                        disabled={isDeleting}
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                    >
                        <MoreHoriz></MoreHoriz>
                    </Button>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                    >
                        <MenuItem onClick={() => {
                            typeof onDuplicate === 'function' && onDuplicate(config)
                            handleClose()
                        }}>

                            <Typography variant='caption'> {t('tracks:duplicate')}</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            typeof onEdit === 'function' && onEdit(config)
                            handleClose()
                        }}>

                            <Typography variant='caption'> {t('button:edit')}</Typography>

                        </MenuItem>


                        <MenuItem onClick={() => {
                            typeof toggleEnabled === 'function' && toggleEnabled(getConfigId(), !config.active)
                            handleClose()
                        }}>
                            <Typography variant='caption'> {t(config.active ? 'disable' : 'enable')}</Typography>
                        </MenuItem>



                        <MenuItem onClick={() => {
                            typeof onDelete === 'function' && onDelete(getConfigId(), config['@type'])
                            handleClose()
                        }}>
                            <Typography color='error' variant='caption'> {t('button:delete')}</Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
            <Divider></Divider>
            <Box sx={{ px: 3, py: 2, flex: 1, width: '100%' }} display={'flex'} flexDirection={'column'}
                alignItems={'self-start'} position={'relative'}>

                {/* If config is disabled we set a black layer to show its disabled */}
                {
                    !config.active && <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 100, backgroundColor: 'rgba(0,0,0,.5)' }}></Box>
                }

                <Button variant='text' size='small' onClick={() => setOpenConfig(!openConfig)}
                    startIcon={openConfig ? <VisibilityOff></VisibilityOff> : <Visibility></Visibility>}
                    sx={{ mt: 1, fontSize: '14px', color: 'white' }}>{t('configuration')} {getConfigRecap()}</Button>
                <Collapse in={openConfig} sx={{ width: '100%', px: 3 }}>
                    <Stack direction={'column'} spacing={1} mt={1}>


                        <Typography variant='caption' fontWeight={500} display={'flex'} flexWrap={'wrap'}
                            alignItems={'center'} justifyContent={'space-between'}>
                            {t('horses')}
                            {
                                isEmpty(horseNames) ?
                                    <Typography variant='caption' fontWeight={400}
                                        color={'GrayText'}>{t('configuration:allHorses')}</Typography>
                                    : <ShowMore items={horseNames} maxLength={3} renderItem={(item, i) => (
                                        <Typography key={i} variant='caption' fontWeight={400}
                                            color={'GrayText'}>{`${item} ${horseNames.length - 1 !== i ? " - " : ''}`}</Typography>
                                    )}></ShowMore>
                            }
                        </Typography>
                        <Typography variant='caption' fontWeight={500} display={'flex'} flexWrap={'wrap'}
                            alignItems={'center'} justifyContent={'space-between'}>
                            {t('editTrainingForm:trackName:label')}
                            {
                                isEmpty(tracks) ?
                                    <Typography variant='caption' fontWeight={400}
                                        color={'GrayText'}>{t('configuration:allTracks')}</Typography>
                                    : <ShowMore items={tracks} maxLength={3} renderItem={(item, i) => (
                                        <Typography key={i} variant='caption' fontWeight={400}
                                            color={'GrayText'}>{`${item} ${tracks.length - 1 !== i ? " - " : ''}`}</Typography>
                                    )}></ShowMore>
                            }

                        </Typography>
                        <Typography variant='caption' fontWeight={500} display={'flex'} flexWrap={'wrap'}
                            alignItems={'center'} justifyContent={'space-between'}>
                            {t('editTrainingForm:trainingType:label')}
                            {
                                isEmpty(trainings) ?
                                    <Typography variant='caption' fontWeight={400}
                                        color={'GrayText'}>{t('configuration:allTypes')}</Typography>
                                    : <ShowMore items={trainings} maxLength={3} renderItem={(item, i) => (
                                        <Typography key={i} variant='caption' fontWeight={400}
                                            color={'GrayText'}>{`${item} ${trainings.length - 1 !== i ? " - " : ''}`}</Typography>
                                    )}></ShowMore>
                            }</Typography>

                        {"horse_ages" in config && isArray(config?.horse_ages) && <Typography variant='caption' fontWeight={500} display={'flex'} flexWrap={'wrap'}
                            alignItems={'center'} justifyContent={'space-between'}>
                            {t('age')}
                            {
                                isEmpty(config?.horse_ages) ?
                                    <Typography variant='caption' fontWeight={400}
                                        color={'GrayText'}>{t('configuration:allAges')}</Typography>
                                    : <Typography variant='caption' fontWeight={400}
                                        color={'GrayText'}>{t('configuration:someAges', {
                                            ages: formatHorsesAgesStr(config.horse_ages)
                                        })}</Typography>
                            }</Typography>}

                    </Stack>

                </Collapse>
                <Button sx={{ color: 'white' }} variant='text' startIcon={
                    <EventRepeat></EventRepeat>}>{t('configuration:triggers')} {!isEmpty(getResume()) && `(${getResume()})`}</Button>
                <Button sx={{ color: 'white' }} onClick={() => setOpenRecipient(!openRecipient)} variant='text'
                    startIcon={<Send></Send>}>{t('configuration:recipient')} {getChannelsRecap()}</Button>
                <Collapse in={openRecipient} sx={{ width: '100%', px: 3 }}>
                    {
                        !isEmpty(channels) && <>
                            <Divider></Divider>
                            <Box width={'100%'} mt={1}>
                                {channels.map((channel, i) => (
                                    <>
                                        <Box display={'flex'} justifyContent={'space-between'} my={1}>
                                            <Typography fontWeight={500} variant='caption' display={'flex'}
                                                alignItems={'center'} gap={1}>
                                                {channel['@type'] === "EmailCommunicationChannel" ?
                                                    <Email sx={{ color: '#0072C6' }} fontSize='small'></Email> : <WhatsApp
                                                        sx={{ color: '#25D366' }}
                                                        fontSize='small'></WhatsApp>} {
                                                    channel.own_private_channel ? t('channel:onlyMe') :
                                                        channel?.name ?? '-'

                                                }</Typography>
                                            <ShowMore
                                                items={channel.targets}
                                                maxLength={3}
                                                renderItem={(item, i) => (
                                                    <Chip sx={{ color: 'white' }} color='info' key={i} size='small'
                                                        label={item}></Chip>
                                                )}
                                            ></ShowMore>
                                        </Box>
                                        {
                                            channels.length - 1 !== i && <Divider></Divider>
                                        }

                                    </>
                                ))}
                            </Box>
                        </>
                    }

                </Collapse>
            </Box>
        </Box>

    )
}

export default ConfigItem